import { MAZE_CELL_SIZE } from '@/constants/maze';
import { getCurrentDemoLevel, useDemoStore } from '@/store/demo';
import { APP_COLOR } from '@/theme/color';
import { getPreloadedAssetUrl } from '@/utils/asset-manifest';
import { isDemoTutorialActive, type DemoTutorialStep } from '@/utils/maze/demo-tutorial';
import type { CSSProperties } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DemoTutorialScrimProps {
  readonly show: boolean;
  readonly className?: string;
}

export function DemoTutorialScrim({ show, className }: DemoTutorialScrimProps) {
  if (!show) {
    return null;
  }

  const scrimStyle: CSSProperties = { background: APP_COLOR.overlay };

  return (
    <div className={`pointer-events-none ${className ?? ''}`} style={scrimStyle} aria-hidden />
  );
}

export function DemoTutorialBlurOverlay() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[30]"
      style={{
        background: '#0000000d',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
      aria-hidden
    />
  );
}

export function DemoTutorialTapLayer() {
  const { t } = useTranslation();
  const demoTutorialStep = useDemoStore((s) => s.demoTutorialStep);
  const advanceDemoTutorial = useDemoStore((s) => s.advanceDemoTutorial);

  if (!isDemoTutorialActive(demoTutorialStep)) {
    return null;
  }

  return (
    <div className="absolute inset-0 z-[200]">
      <button
        type="button"
        className="absolute inset-0 cursor-pointer border-0 bg-transparent p-0"
        aria-label={t('tutorial.tapToContinue')}
        onClick={() => {
          void advanceDemoTutorial();
        }}
      />
    </div>
  );
}

// ── Hand overlay ─────────────────────────────────────────────────────────────

type ActiveStep = 'ball' | 'controls' | 'path' | 'zoom';
type StepPositionKind = 'ball' | 'portal' | 'controls' | 'center';

interface StepConfig {
  posKind: StepPositionKind;
  num: number;
  labelKey: string;
  tooltipDir: 'right' | 'above' | 'left';
  showGlowRing: boolean;
}

const STEP_CONFIG: Record<ActiveStep, StepConfig> = {
  ball: {
    posKind: 'ball',
    num: 1,
    labelKey: 'tutorial.steps.ball',
    tooltipDir: 'right',
    showGlowRing: true,
  },
  controls: {
    posKind: 'controls',
    num: 2,
    labelKey: 'tutorial.steps.controls',
    tooltipDir: 'above',
    showGlowRing: false,
  },
  path: {
    posKind: 'portal',
    num: 3,
    labelKey: 'tutorial.steps.path',
    tooltipDir: 'left',
    showGlowRing: true,
  },
  zoom: {
    posKind: 'center',
    num: 4,
    labelKey: 'tutorial.steps.zoom',
    tooltipDir: 'above',
    showGlowRing: false,
  },
};

// Finger tip position within the 34×60 rendered hand-icon.svg
const HAND_W = 34;
const HAND_H = 60;
const FINGER_TIP_X = 12;
const FINGER_TIP_Y = 2;

const PINCH_W = 48;
const PINCH_H = 48;

function isActiveStep(step: DemoTutorialStep): step is ActiveStep {
  return step === 'ball' || step === 'controls' || step === 'path' || step === 'zoom';
}

function tooltipTransform(dir: StepConfig['tooltipDir']): string {
  if (dir === 'right') return 'translate(16px, -50%)';
  if (dir === 'left') return 'translate(calc(-100% - 16px), -50%)';
  return 'translate(-50%, calc(-100% - 10px))';
}

interface CanvasMetrics {
  canvasLeft: number;
  canvasTop: number;
  scaleX: number;
  scaleY: number;
}

export function DemoTutorialHandOverlay() {
  const { t } = useTranslation();
  const step = useDemoStore((s) => s.demoTutorialStep);
  const demoLevel = useDemoStore(getCurrentDemoLevel);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<CanvasMetrics | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  // Measure canvas position relative to the page-level wrapper.
  // scaleX/scaleY convert PixiJS canvas pixels to CSS pixels, using the
  // canvas HTML width/height attributes (set by app.renderer.resize) as the
  // PixiJS coordinate space reference.
  useLayoutEffect(() => {
    const measure = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const canvas = document.querySelector('canvas') as HTMLCanvasElement | null;
      if (!canvas) return;
      const kr = canvas.getBoundingClientRect();
      if (kr.width === 0 || kr.height === 0) return;
      const wr = wrapper.getBoundingClientRect();
      setMetrics({
        canvasLeft: kr.left - wr.left,
        canvasTop: kr.top - wr.top,
        scaleX: canvas.width > 0 ? kr.width / canvas.width : 0,
        scaleY: canvas.height > 0 ? kr.height / canvas.height : 0,
      });
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    const main = wrapperRef.current?.parentElement;
    if (main) resizeObserver.observe(main);

    // Re-measure at PixiJS layout-update intervals so the canvas is sized
    // before we lock in the metrics.
    const timers = [50, 150, 300, 600, 1000].map((d) =>
      setTimeout(() => {
        const canvas = document.querySelector('canvas');
        if (canvas) resizeObserver.observe(canvas);
        measure();
      }, d),
    );

    return () => {
      resizeObserver.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  // Recompute the pixel target whenever the step, canvas metrics, or level change.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!isActiveStep(step) || !wrapper) {
      setPos(null);
      return;
    }

    const cfg = STEP_CONFIG[step];

    if (cfg.posKind === 'ball' || cfg.posKind === 'portal') {
      if (!metrics || !demoLevel) {
        setPos(null);
        return;
      }
      const col = cfg.posKind === 'ball' ? demoLevel.startCol : demoLevel.endCol;
      const row = cfg.posKind === 'ball' ? demoLevel.startRow : demoLevel.endRow;
      setPos({
        x: metrics.canvasLeft + (col * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2) * metrics.scaleX,
        y: metrics.canvasTop + (row * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2) * metrics.scaleY,
      });
      return;
    }

    // Zoom step: finger tip points to center of the canvas.
    if (cfg.posKind === 'center') {
      const canvas = document.querySelector('canvas') as HTMLCanvasElement | null;
      if (!canvas || !wrapper) {
        setPos(null);
        return;
      }
      const wr = wrapper.getBoundingClientRect();
      const kr = canvas.getBoundingClientRect();
      setPos({
        x: kr.left - wr.left + kr.width / 2,
        y: kr.top - wr.top + kr.height / 2,
      });
      return;
    }

    // Controls step: finger tip points at the top-center of the D-pad grid
    // (where the UP arrow button lives).
    const controlsEl = document.querySelector('[data-tutorial-controls]') as HTMLElement | null;
    if (!controlsEl) {
      setPos(null);
      return;
    }
    const wr = wrapper.getBoundingClientRect();
    const cr = controlsEl.getBoundingClientRect();
    setPos({
      x: cr.left + cr.width / 2 - wr.left,
      y: cr.top - wr.top + 15,
    });
  }, [step, metrics, demoLevel]);

  const active = isActiveStep(step);

  return (
    <div ref={wrapperRef} className="pointer-events-none absolute inset-0 z-[999]" aria-hidden>
      {active && pos
        ? (() => {
            const cfg = STEP_CONFIG[step];
            const { x, y } = pos;
            const tt = tooltipTransform(cfg.tooltipDir);
            return (
              <div key={step}>
                {/* Glow ring — canvas steps only */}
                {cfg.showGlowRing && (
                  <div
                    style={{
                      position: 'absolute',
                      left: x,
                      top: y,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <div
                      className="dtutorial-glow"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: `1.5px solid ${APP_COLOR.info}`,
                        boxShadow: `0 0 10px ${APP_COLOR.infoGlow}`,
                        animation:
                          'dtutorial-appear 0.3s ease-out both, dtutorial-pulse 1.1s ease-in-out 0.3s infinite',
                      }}
                    />
                  </div>
                )}
                {/* Tooltip badge */}
                <div
                  className="dtutorial-tooltip"
                  style={{
                    position: 'absolute',
                    left: x,
                    top: y,
                    transform: tt,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    background: 'rgba(0,0,0,0.78)',
                    border: `1.5px solid ${APP_COLOR.info}`,
                    borderRadius: 999,
                    padding: '3px 10px 3px 4px',
                    whiteSpace: 'nowrap',
                    animation: 'dtutorial-appear 0.25s ease-out 0.1s both',
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: APP_COLOR.info,
                      color: '#000',
                      fontSize: 10,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {cfg.num}
                  </span>
                  <span
                    style={{
                      color: APP_COLOR.infoBright,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {t(cfg.labelKey)}
                  </span>
                </div>
                {/* Hand cursor — finger tip aligned to (x, y); pinch icon for zoom step */}
                {step === 'zoom' ? (
                  <div
                    style={{
                      position: 'absolute',
                      left: x,
                      top: y,
                      transform: 'translate(-50%, -19%)',
                    }}
                  >
                    <div
                      className="dtutorial-hand"
                      style={{ animation: 'dtutorial-tap 2s ease-in-out 0.5s infinite' }}
                    >
                      <img
                        src={getPreloadedAssetUrl('/assets/pinch.png')}
                        alt=""
                        width={PINCH_W}
                        height={PINCH_H}
                        draggable={false}
                        style={{
                          display: 'block',
                          filter: `drop-shadow(0 2px 8px rgba(0,0,0,0.6)) drop-shadow(0 0 10px ${APP_COLOR.info}88)`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      position: 'absolute',
                      left: x,
                      top: y,
                      transform: `translate(-${FINGER_TIP_X}px, -${FINGER_TIP_Y}px)`,
                    }}
                  >
                    <div
                      className="dtutorial-hand"
                      style={{
                        transformOrigin: `${FINGER_TIP_X}px ${FINGER_TIP_Y}px`,
                        animation: 'dtutorial-tap 2s ease-in-out 0.5s infinite',
                      }}
                    >
                      <img
                        src={getPreloadedAssetUrl('/assets/hand-icon.svg')}
                        alt=""
                        width={HAND_W}
                        height={HAND_H}
                        draggable={false}
                        style={{
                          display: 'block',
                          filter: `drop-shadow(0 2px 8px rgba(0,0,0,0.6)) drop-shadow(0 0 10px ${APP_COLOR.info}88)`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        : null}
    </div>
  );
}

export function DemoTutorialTapPrompt() {
  const { t } = useTranslation();

  return (
    <div
      className="pointer-events-none fixed left-1/2 z-[1000] -translate-x-1/2"
      style={{ top: 40 }}
    >
      <p
        className="rounded-full px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] motion-reduce:animate-none animate-pulse"
        style={{
          color: '#ffffff',
          background: APP_COLOR.panelSoft,
          border: `1px solid ${APP_COLOR.infoSoft40}`,
          boxShadow: `0 0 18px ${APP_COLOR.infoGlow}`,
        }}
      >
        {t('tutorial.tapToContinue')}
      </p>
    </div>
  );
}
